import { useRef } from 'react';
import { useMobileTranslation } from '@/hooks/useMobileTranslation';
import GlassCard from '@/components/GlassCard';
import PressBanner from '@/components/PressBanner';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { getLocalizedPath } from '@/lib/localizedPath';
import { getOptimizedStorageUrl, getStorageSrcSet } from '@/lib/responsiveImages';

const DOMINO_URL = 'https://nzvyxkbsowtinhywugwn.supabase.co/storage/v1/object/public/blog-images/drk-Domino_21.jpg';
const heroSrcSet = getStorageSrcSet(DOMINO_URL, [480, 768, 1024, 1280, 1600]);
const heroFallback = getOptimizedStorageUrl(DOMINO_URL, 1600);
const heroSizes = '(min-width: 1280px) 1280px, 100vw';
// Tiny LQIP (~1-2 KiB) served from Supabase render API and blurred via CSS.
// Painted instantly as the section background so users see something while
// the full hero image streams in.
const heroLqip = getOptimizedStorageUrl(DOMINO_URL, 32, 30);

const HeroSectionV2 = () => {
  const { tm, t, language } = useMobileTranslation();
  const heroImgRef = useRef<HTMLImageElement>(null);
  // No static placeholder in index.html: visitors must only ever see the real
  // page. The hero image is preloaded in index.html so it still paints fast.

  return (
    <>
      {/* Section 1: Full-width Hero with Background.
          The blurred LQIP is painted via background-image so it appears
          immediately on first paint, eliminating perceived render delay
          while the full-resolution hero image streams in. */}
      <section
        className="relative min-h-[60vh] md:min-h-[55vh] flex items-center justify-start overflow-hidden bg-cover bg-center"
        style={{
          backgroundImage: `url("${heroLqip}")`,
          backgroundColor: 'hsl(var(--muted))',
        }}
      >
        {/* Blur layer over the LQIP background so pixelation reads as bokeh */}
        <div
          aria-hidden="true"
          className="absolute inset-0 backdrop-blur-2xl"
        />
        {/* LCP Image — paints from the preload cache, no fade. */}
        <img
          ref={heroImgRef}
          src={heroFallback}
          srcSet={heroSrcSet}
          alt=""
          aria-hidden="true"
          fetchPriority="high"
          loading="eager"
          decoding="async"
          width={1920}
          height={1080}
          // Hero is full-bleed (object-cover, absolute inset-0) so width === viewport.
          // Cap at 1600px on wide screens — the image is decorative (alt="")
          // and detail beyond 1600 CSS px is imperceptible, but on HiDPI displays
          // a naïve "100vw" would force the 2560w candidate on every >1280 CSS px
          // viewport. Capping cuts ~40% of bytes on 1440p / 4K monitors.
          sizes={heroSizes}
          onLoad={(e) => {
            // Remove the static pre-hydration LCP placeholder from index.html
            // once the React hero image has painted. The static node lives in
            // the DOM AFTER #root and was visually overlapping React content
            // (header + hero text), making the page look broken.

            // Dev-only verification: confirm the rendered candidate matches a
            // <link rel="preload" as="image"> entry and that the browser
            // actually reused the preloaded response (transferSize === 0).
            if (import.meta.env.DEV) {
              const img = e.currentTarget as HTMLImageElement;
              const rendered = img.currentSrc;
              const preloads = Array.from(
                document.querySelectorAll<HTMLLinkElement>(
                  'link[rel="preload"][as="image"]'
                )
              );
              const preloadHrefs = preloads.flatMap((l) => {
                const set = (l as HTMLLinkElement & { imageSrcset?: string })
                  .imageSrcset;
                if (set) {
                  return set.split(',').map((s) => s.trim().split(/\s+/)[0]);
                }
                return [l.href];
              });
              const matched = preloadHrefs.includes(rendered);

              const entry = performance
                .getEntriesByType('resource')
                .find(
                  (r) => (r as PerformanceResourceTiming).name === rendered
                ) as
                | (PerformanceResourceTiming & { deliveryType?: string })
                | undefined;
              const reused =
                entry !== undefined &&
                (entry.transferSize === 0 ||
                  entry.deliveryType === 'cache' ||
                  entry.initiatorType === 'link');

              // eslint-disable-next-line no-console
              console.info(
                '%c[HeroPreload]',
                'color:#719475;font-weight:bold',
                {
                  rendered,
                  matchesPreload: matched,
                  reusedFromPreload: reused,
                  initiatorType: entry?.initiatorType,
                  transferSize: entry?.transferSize,
                  deliveryType: entry?.deliveryType,
                }
              );
              if (!matched) {
                console.warn(
                  '[HeroPreload] rendered URL not found in any <link rel="preload"> — preload is being wasted'
                );
              }
            }
          }}
          // No opacity fade: the image is preloaded with high priority and
          // reused from cache, so it paints immediately. Removing the
          // 300ms transition cuts ~300ms off the LCP "element render delay".
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        
        {/* Left-aligned Headline Content */}
        <div className="relative z-10 py-16 pl-[10%] pt-[200px] md:py-[173px] pb-[161px] pr-[116px] px-[49px]">
          <div className="max-w-2xl">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-graphit leading-[1.1] animate-fade-in">
              {t('hero.line1', 'Klar denken.')}
              <br />
              <span className="text-korall">{t('hero.line2', 'Effektiv handeln.')}</span>
              <br />
              {t('hero.line3', 'Vital bleiben.')}
            </h1>
            <p className="text-graphit/70 text-lg md:text-xl mt-6 animate-fade-in font-bold px-0">
              {tm('hero.tagline', 'Performance beginnt bei dir! Das DrK System für eigenverantwortliche High Performer.')}
            </p>
          </div>
        </div>
      </section>

      {/* Press Banner between sections */}
      <PressBanner />

      {/* Section 2: GlassCard with Content */}
      <section className="relative py-20 md:py-[140px] -mt-1 overflow-hidden bg-background">
        <div className="container-blog relative z-10">
          <GlassCard variant="floating" className="bg-transparent border-0 p-0 md:p-0 lg:p-0">
            <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-center">
              {/* Image Left - 2 columns */}
              <div className="lg:col-span-2 flex-col items-center flex lg:items-center justify-start">
                <div className="relative">
                  <picture>
                    <source
                      type="image/webp"
                      srcSet="/lovable-uploads/2433e23d-portrait-600.webp 600w, /lovable-uploads/2433e23d-portrait-900.webp 900w"
                      sizes="(max-width: 640px) 224px, (max-width: 768px) 256px, 288px"
                    />
                    <img
                      src="/lovable-uploads/2433e23d-portrait-600.webp"
                      alt="Jürgen Klanert, PhD - Biohacking-Experte und Health Coach für Führungskräfte"
                      loading="eager"
                      fetchPriority="high"
                      decoding="async"
                      width={288}
                      height={384}
                      className="relative w-56 sm:w-64 md:w-72 lg:w-64 xl:w-72 aspect-[3/4] object-cover object-top rounded-2xl"
                    />
                  </picture>
                </div>
                <p className="text-graphit/75 text-sm font-light mt-4 text-center lg:text-left">Jürgen Klanert, PhD</p>
              </div>

              {/* Content Right - 3 columns */}
              <div className="lg:col-span-3 space-y-6 md:space-y-8">
                <div className="space-y-4">
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-graphit leading-[1.25]">
                    {t('hero.v2.title', 'Erlebe mein wissenschaftlich fundiertes Health System')}
                  </h2>
                  <p className="text-graphit/70 text-base md:text-lg font-light leading-[1.7] max-w-xl">
                    {tm('hero.subtitle', 'Wissenschaftlich fundiertes Health System – als Kurse, Buch und begleitete Programme. Struktur statt Selbstoptimierung. Wirkung statt Versprechen.')}
                  </p>
                </div>

                {/* Dynamic CTA - based on decidePrimaryCTA */}
                <div className="pt-2 flex flex-wrap items-center gap-4">
                  <Button asChild size="lg">
                    <Link to={getLocalizedPath('/courses', language)}>
                      {t('hero.cta.courses', language === 'en' ? 'Discover courses' : 'Kurse entdecken')}
                    </Link>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <Link to={getLocalizedPath('/system', language)}>
                      {t('hero.cta.system', language === 'en' ? 'The system' : 'Das System')}
                    </Link>
                  </Button>
                </div>

                {/* Trust Indicators */}
                <div className="pt-6 border-t border-graphit/10">
                  <div className="flex flex-wrap items-center gap-6 sm:gap-8 md:gap-10">
                    <div className="text-center sm:text-left">
                      <p className="text-2xl md:text-3xl font-semibold text-graphit">20+</p>
                      <p className="text-graphit/70 text-xs sm:text-sm">{t('hero.trust.years', 'Jahre Erfahrung')}</p>
                    </div>
                    <div className="w-px h-10 bg-graphit/20 hidden sm:block" />
                    <div className="text-center sm:text-left max-w-[160px]">
                      <p className="text-sm md:text-base font-semibold text-graphit leading-snug">
                        {t('hero.trust.system', 'Eigenes High-Performance System')}
                      </p>
                      <p className="text-graphit/70 text-xs sm:text-sm">
                        {t('hero.trust.sustainability', 'für nachhaltige Leistungsfähigkeit')}
                      </p>
                    </div>
                    <div className="w-px h-10 bg-graphit/20 hidden sm:block" />
                    <div className="text-center sm:text-left">
                      <p className="text-2xl md:text-3xl font-semibold text-graphit">PhD</p>
                      <p className="text-graphit/70 text-xs sm:text-sm">{t('hero.trust.science', 'Wissenschaftlich fundiert')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </GlassCard>
        </div>
      </section>
    </>
  );
};

export default HeroSectionV2;
